import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ToolCallState } from './types';

// One rule for every wait: a named step, where the answer will land, that is
// only ever replaced by its result (FLA-8232). The old loader hid itself the
// moment any text streamed, which is exactly when the assistant pauses to run
// a tool — so the wait that most needed a cue was the one with none.
//
// A step per tool the assistant has run, plus a trailing "Writing the answer"
// once the tools are done and the reply is still coming. When the turn ends the
// trail folds into a single line you can open again.

const STALL_MS = 15000;
// How long after the last token the reply still counts as "arriving". Long
// enough to bridge the pauses between chunks, short enough that a tool call
// brings the line back rather than leaving the screen silent.
const TEXT_IDLE_MS = 700;

// Friendly label shown while a tool runs. Explicit labels read best; any tool
// without one (incl. tools added later) falls back to a verb-based category
// label, and finally a generic "Working on it" — so the raw camelCase tool
// name is NEVER shown to the user.
export const getToolMessage = (t: TFunction, toolName: string): string => {
  const messages: Record<string, string> = {
    getMyProfile: t('aiChat.tools.getMyProfile'),
    getMyAbsenceBalance: t('aiChat.tools.getMyAbsenceBalance'),
    searchDocuments: t('aiChat.tools.searchDocuments'),
    requestAbsence: t('aiChat.tools.requestAbsence'),
    getMyAbsences: t('aiChat.tools.getMyAbsences'),
    getTeamAbsences: t('aiChat.tools.getTeamAbsences'),
    getMyTimeBalance: t('aiChat.tools.getMyTimeBalance'),
    getMyTimesheets: t('aiChat.tools.getMyTimesheets'),
    searchKnowledgeBase: t('aiChat.tools.searchKnowledgeBase'),
    getMyDocuments: t('aiChat.tools.getMyDocuments'),
    getMyTeam: t('aiChat.tools.getMyTeam'),
    getMyShifts: t('aiChat.tools.getMyShifts'),
    getMySurveys: t('aiChat.tools.getMySurveys'),
    getMyTickets: t('aiChat.tools.getMyTickets'),
    getMyExpenses: t('aiChat.tools.getMyExpenses'),
    lookupPerson: t('aiChat.tools.lookupPerson'),
    searchCompanyPolicies: t('aiChat.tools.searchCompanyPolicies'),
    getAvailableQueries: t('aiChat.tools.getAvailableQueries'),
    executeGraphQLQuery: t('aiChat.tools.executeGraphQLQuery'),
    suggestQuickAction: t('aiChat.tools.suggestQuickAction'),
  };
  return messages[toolName] ?? getToolCategoryMessage(t, toolName);
};

// Verb-prefix fallback so unmapped tools still read as human status, never the
// raw identifier. Reads (get/search/lookup + the GraphQL escape hatch) →
// "Looking that up"; creates (create/add/submit/request) → "Submitting";
// state changes (delete/clock/pause/resume) → "Updating"; anything else →
// "Working on it".
const getToolCategoryMessage = (t: TFunction, toolName: string): string => {
  if (
    /^(get|search|lookup)/.test(toolName) ||
    toolName === 'executeGraphQLQuery'
  ) {
    return t('aiChat.generic.lookingUp');
  }
  if (/^(create|add|submit|request)/.test(toolName)) {
    return t('aiChat.generic.submitting');
  }
  if (/^(delete|clock|pause|resume)/.test(toolName)) {
    return t('aiChat.generic.updating');
  }
  return t('aiChat.generic.working');
};

// While nothing has run yet there is no tool to name, so rotate a generic line
// rather than showing a bare spinner.
const getGenericMessages = (t: TFunction): string[] => [
  t('aiChat.generic.thinking'),
  t('aiChat.generic.searchingData'),
  t('aiChat.generic.analyzing'),
  t('aiChat.generic.readingDocs'),
];

function useGenericMessage(active: boolean): string {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const messages = getGenericMessages(t);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    const interval = setInterval(
      () => setIndex((prev) => (prev + 1) % messages.length),
      2500,
    );
    return () => clearInterval(interval);
  }, [active, messages.length]);

  return messages[index];
}

// A turn that has produced no news for 15s says so. Without this a hung run
// looks identical to a working one, which is how the original report described
// it: dead air with no recovery cue.
function useIsStalled(isLoading: boolean, changeKey: string): boolean {
  const [isStalled, setIsStalled] = useState(false);

  useEffect(() => {
    setIsStalled(false);
    if (!isLoading) return;
    const timer = setTimeout(() => setIsStalled(true), STALL_MS);
    return () => clearTimeout(timer);
  }, [isLoading, changeKey]);

  return isStalled;
}

// Text arriving on screen IS the progress indicator — a spinner next to
// streaming words is two things saying the same thing. So the line hides while
// words are landing and comes back if they stop, which is exactly the gap a
// tool call opens up (FLA-8232).
function useTextIsFlowing(streamedLength: number): boolean {
  const [isFlowing, setIsFlowing] = useState(false);

  useEffect(() => {
    if (streamedLength === 0) {
      setIsFlowing(false);
      return;
    }
    setIsFlowing(true);
    const timer = setTimeout(() => setIsFlowing(false), TEXT_IDLE_MS);
    return () => clearTimeout(timer);
  }, [streamedLength]);

  return isFlowing;
}

type Props = {
  // True only for the assistant message currently being written.
  isStreaming: boolean;
  activeToolName: string | null;
  // Tool calls of this assistant message.
  toolCalls: ToolCallState[];
  // How far the reply has streamed. Text that keeps arriving is not a stall.
  streamedLength: number;
  onRetry?: () => void;
};

const AiChatProgress: React.FC<React.PropsWithChildren<Props>> = ({
  isStreaming,
  activeToolName,
  toolCalls,
  streamedLength,
  onRetry,
}) => {
  const { t } = useTranslation();

  const lastTool = toolCalls[toolCalls.length - 1];
  const genericMessage = useGenericMessage(isStreaming && !lastTool);
  const textIsFlowing = useTextIsFlowing(streamedLength);
  const isStalled = useIsStalled(
    isStreaming,
    `${toolCalls.length}:${activeToolName ?? ''}:${streamedLength}`,
  );

  // Nothing to report once the turn is over, or while the answer is visibly
  // writing itself.
  if (!isStreaming || textIsFlowing) return null;

  // One line, not a stack: what is happening right now. Naming each tool that
  // already ran turned a two-tool answer into a wall of near-identical lines.
  const label = activeToolName
    ? getToolMessage(t, activeToolName)
    : lastTool
    ? getToolMessage(t, lastTool.toolName)
    : genericMessage;

  return (
    <div className="ai-chat-progress" data-testid="ai-chat-progress">
      <div
        className={`ai-chat-progress-step ai-chat-progress-step--live${
          isStalled ? ' ai-chat-progress-step--stalled' : ''
        }`}
        aria-live="polite">
        <span className="ai-chat-progress-mark" aria-hidden="true" />
        <span>{isStalled ? t('aiChat.progress.stalled') : label}</span>
      </div>
      {isStalled && onRetry && (
        <button
          type="button"
          className="ai-chat-progress-retry"
          onClick={onRetry}
          data-testid="ai-chat-progress-retry">
          {t('aiChat.progress.stopAndRetry')}
        </button>
      )}
    </div>
  );
};

export default AiChatProgress;
